import { Dialog, DialogActions, DialogContent, DialogContentText } from '@mui/material';
import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Confirm, Button as RaButton, useTranslate } from 'react-admin';

import { DialogTitleWithX } from '@sh4res/bo-common/components';
import { TranslationKeys } from '@sh4res/bo-common/i18n/types';

interface UseConfirmDialogProps {
  title: TranslationKeys;
  content: TranslationKeys;
  /**
   * display a close button in the top right instead of a "cancel" cta
   * should be used especially when the modal is for a cancel action to avoid confusion
   */
  showCancelX?: boolean;
}

/**
 * hook to display a confirm dialog
 *
 * @example
 * const confirmDialog = useConfirmDialog({
 *   title: 'resources.users...',
 *   content: 'resources.users...',
 * });
 *
 * // somewhere in a callback
 * if(confirmDialog.confirm()) {
 *   ...
 * }
 *
 * return <>
 *   ...
 *   {confirmDialog.Component}
 * </>
 */
export const useConfirmDialog = ({
  title: titleKey,
  content: contentKey,
  showCancelX,
}: UseConfirmDialogProps) => {
  // Narrow to `string` to keep the big TranslationKeys union out of the memo deps (TS2590).
  const title: string = titleKey;
  const content: string = contentKey;
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [translateOptions, setTranslateOptions] = useState<Record<string, unknown>>();
  const ref = useRef<(value: boolean) => void>();

  const handleDialogOpen = useCallback((options?: Record<string, unknown>) => {
    setTranslateOptions(options);
    setOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setOpen(false);
    ref.current?.(false);
    ref.current = undefined;
  }, []);

  const handleDialogConfirm = useCallback(() => {
    setOpen(false);
    ref.current?.(true);
    ref.current = undefined;
  }, []);

  const confirm = useCallback(
    async (translateOptions?: Record<string, unknown>): Promise<boolean> => {
      handleDialogOpen(translateOptions);
      return new Promise((resolve) => {
        ref.current = resolve;
      });
    },
    [handleDialogOpen],
  );

  return useMemo(
    () => ({
      confirm,
      Component: showCancelX ? (
        <Dialog
          open={open}
          onClose={handleDialogClose}
          onClick={(event: MouseEvent) => event.stopPropagation()}
          fullWidth
          maxWidth="sm">
          <DialogTitleWithX onClose={handleDialogClose}>
            {translate(title, translateOptions)}
          </DialogTitleWithX>
          <DialogContent>
            <DialogContentText>{translate(content, translateOptions)}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <RaButton
              data-testid="BoConfirmDialog_ConfirmButton"
              onClick={handleDialogConfirm}
              label="ra.action.confirm"
            />
          </DialogActions>
        </Dialog>
      ) : (
        <Confirm
          isOpen={open}
          title={title}
          content={content}
          translateOptions={translateOptions}
          onConfirm={handleDialogConfirm}
          onClose={handleDialogClose}
        />
      ),
    }),
    [
      confirm,
      open,
      handleDialogConfirm,
      handleDialogClose,
      title,
      content,
      translateOptions,
      showCancelX,
      translate,
    ],
  );
};
